import Box from "@/common/components/Box"
import { ChangeHistory } from "@/common/components/ChangeHistory"
import { FormTransformers } from "@/common/utils/formTransformers"
import { AccreditationInformationSection } from "@/features/establishmentLeadsFunnel/components/AccreditationInformationSection"
import { AddressSection } from "@/features/establishmentLeadsFunnel/components/AddressSection"
import { BankAccountInformationSection } from "@/features/establishmentLeadsFunnel/components/BankAccountInformationSection"
import { ContactsSection } from "@/features/establishmentLeadsFunnel/components/ContactsSection"
import { GeneralInformationSection } from "@/features/establishmentLeadsFunnel/components/GeneralInformationSection"
import { GenericDocumentsSection } from "@/features/establishmentLeadsFunnel/components/GenericDocumentsSection"
import { LeadHeader } from "@/features/establishmentLeadsFunnel/components/LeadHeader"
import { ResponsibleSection } from "@/features/establishmentLeadsFunnel/components/ResponsibleSection"
import { stateStrategy, translateStateStrategy } from "@/features/establishmentLeadsFunnel/constants/stateStrategy.constant"
import { useEstablishmentLeadForm } from "@/features/establishmentLeadsFunnel/hooks/useEstablishmentLeadForm"
import { EstablishmentLeadsFunnelUpdateValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { EstablishmentLeadData } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type"
import { Checklist } from "@/features/privateCompanyLeadsFunnel/components/Checklist"
import { useQueryClient } from "@tanstack/react-query"
import { FormProvider, SubmitHandler } from "react-hook-form"
import { useUpdateEstablishmentLeadManagers } from "../../hooks/useUpdateEstablishmentLeadManagers"
import { useUpdateEstablishmentLead } from "../../hooks/useUpdateEstablishmentLead"

export const LeadQualifyingClientChangesRequestedForm = ({
  id,
  establishmentLead,
}: {
  id: string,
  establishmentLead: EstablishmentLeadData,
}) => {
  const queryClient = useQueryClient()

  const formMethods = useEstablishmentLeadForm({ establishmentLead })

  const {
    control,
    register,
    setError,
    getValues,
    handleSubmit,
    formState: { errors },
  } = formMethods

  const updateLeadOnSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['establishmentLead', id] })
  }

  const {
    mutate: updateEstablishmentLeadManagers,
    isPending: updateEstablishmentLeadManagersIsPending,
  } = useUpdateEstablishmentLeadManagers({
    id,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<EstablishmentLeadsFunnelUpdateValidationSchema>({ setError })
  })

  const {
    mutate: updateEstablishmentLead,
  } = useUpdateEstablishmentLead({
    id,
    establishmentLead,
    onSuccess: updateLeadOnSuccess,
    onError: FormTransformers.errorsTransformer<EstablishmentLeadsFunnelUpdateValidationSchema>({ setError })
  })

  const submitManagers = async () => {
    updateEstablishmentLeadManagers(getValues());
  };

  const onSubmit: SubmitHandler<EstablishmentLeadsFunnelUpdateValidationSchema> = (data) => updateEstablishmentLead(data)
  const submit = handleSubmit(onSubmit)

  return (
    <FormProvider {...formMethods}>
      <form>
        <LeadHeader
          establishmentLeadId={id}
          display_name={establishmentLead.display_name}
          url={establishmentLead.general_information.company_logo?.url}
          showArchiveButton
          archived_at={establishmentLead.archived_at}
        />
        <div className='flex gap-6'>
          <Box className="w-full flex flex-col gap-6 h-[calc(100vh-130px)] overflow-y-scroll">
            <ResponsibleSection
              establishmentLead={establishmentLead}
              submit={submitManagers}
              submitObservations={submit}
              disabled={updateEstablishmentLeadManagersIsPending}
            />
            <AccreditationInformationSection
              establishmentLead={establishmentLead}
              disabled
            />
            <GeneralInformationSection
              establishmentLeadId={id}
              general_information={establishmentLead.general_information}
              leadState={establishmentLead.state}
              control={control}
              register={register}
              errors={errors}
              disabled
            />
            <AddressSection
              establishmentLeadId={id}
              address={establishmentLead.address}
              leadState={establishmentLead.state}
              errors={errors}
              disabled
            />
            <BankAccountInformationSection
              bank_account_information={establishmentLead.bank_account_information}
              control={control}
              register={register}
              errors={errors}
              disabled
            />
            <ContactsSection
              establishmentLeadId={id}
              contacts={establishmentLead.contacts}
              leadState={establishmentLead.state}
              control={control}
            />
            <GenericDocumentsSection
              establishmentLeadId={id}
              generic_documents={establishmentLead.generic_documents}
              control={control}
              disabled
            />
          </Box>
          <div className="flex flex-col gap-4">
            <Checklist
              requirement_list_summary={establishmentLead.requirement_list_summary}
              state={translateStateStrategy[stateStrategy[establishmentLead.state as keyof typeof stateStrategy]]}
              className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
            />
            <ChangeHistory logs={establishmentLead.logs || []} />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
